import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BedDouble } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "../lib/supabase";
import { getListingAvailability } from "@/lib/roomAvailability";
import { firstImageUrl } from "@/lib/roomImages";

interface MoreRoomsInFlatProps {
  /** Current room — excluded from its own list. */
  roomId: number;
  /** Current room's apartment, when it belongs to one. */
  apartmentId: number | null;
  /** Property city, used only for the fallback row. */
  city: string;
}

export interface SiblingRoom {
  id: number;
  title: string | null;
  price: number | null;
  available: boolean | null;
  available_from: string | null;
  next_available_date: string | null;
  images: unknown;
  size_sqm: number | null;
  floor: string | null;
  is_hidden: boolean | null;
  sort_order: number | null;
  properties?: {
    title: string | null;
    city: string | null;
    is_hidden: boolean | null;
  } | null;
}

/**
 * The generated Database types make postgrest's inference explode
 * (TS2589: "excessively deep") once filters are chained on `rooms`. These are
 * two plain reads, so they go through this minimal view of the builder
 * instead; rows come back as `unknown` and are cast to `SiblingRoom`, which
 * is the shape the select actually returns.
 */
interface RoomsQuery {
  eq(column: string, value: unknown): RoomsQuery;
  neq(column: string, value: unknown): RoomsQuery;
  order(
    column: string,
    options: { ascending: boolean; nullsFirst: boolean }
  ): RoomsQuery;
  limit(count: number): PromiseLike<{
    data: unknown;
    error: { message: string } | null;
  }>;
}

const db = supabase as unknown as {
  from(table: "rooms"): { select(columns: string): RoomsQuery };
};

const ROOM_FIELDS: string =
  "id, title, price, available, available_from, next_available_date, images, size_sqm, floor, is_hidden, sort_order";

const ROOM_FIELDS_WITH_PROPERTY: string = `${ROOM_FIELDS}, properties!inner ( title, city, is_hidden )`;

/** CMS rows that are property image labels rather than bookable rooms. */
const NON_ROOM_TITLES = new Set(["villa", "kitchen", "staircase"]);

/** Same public visibility rule the listing grid applies (see Properties.tsx). */
function isPubliclyListable(room: SiblingRoom): boolean {
  if (room.is_hidden === true) return false;
  if (room.properties?.is_hidden === true) return false;
  return !NON_ROOM_TITLES.has((room.title || "").trim().toLowerCase());
}

/**
 * "More rooms in this flat" (COL-48).
 *
 * Scoped to the current room's own apartment, so a visitor looking at a room
 * in one shared flat is offered its actual flatmate rooms, never an unrelated
 * apartment in the same building. When the flat has nothing else to show —
 * a single-room apartment, or every sibling hidden — it falls back to other
 * rooms in the same city.
 *
 * Occupied rooms are kept and dimmed rather than hidden, matching the listing
 * grid: hiding them made large properties look empty.
 */
/**
 * The selection behind "More rooms in this flat", exported so the rebuilt room
 * page can render the draft's cards from exactly the same rows. Keeping one
 * query here is the point: the apartment scoping, the same-city fallback and
 * the public-visibility filter are the behaviour, and a second copy of them on
 * the page would drift.
 */
export function useMoreRoomsInFlat({
  roomId,
  apartmentId,
  city,
}: MoreRoomsInFlatProps): { rooms: SiblingRoom[]; isCityFallback: boolean } {
  const { data: flatRooms = [], isFetched: flatFetched } = useQuery({
    queryKey: ["room-siblings", apartmentId, roomId],
    enabled: apartmentId != null,
    queryFn: async () => {
      // is_hidden filtered server-side; the CMS-image-label title check
      // still runs client-side in isPubliclyListable (rare, low-volume rows).
      const { data, error } = await db
        .from("rooms")
        .select(ROOM_FIELDS)
        .eq("apartment_id", apartmentId as number)
        .eq("is_hidden", false)
        .neq("id", roomId)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .limit(12);
      if (error) throw error;
      return ((data || []) as unknown as SiblingRoom[]).filter(
        isPubliclyListable
      );
    },
  });

  // Only fall back once the sibling query has actually answered. Keying off
  // `flatRooms.length` alone would treat the loading placeholder `[]` as
  // "this flat has no other rooms", firing the city query on every
  // apartment-linked room and briefly flashing the wrong heading if it won
  // the race.
  const needsFallback =
    apartmentId == null || (flatFetched && flatRooms.length === 0);

  const { data: cityRooms = [] } = useQuery({
    queryKey: ["room-city-alternatives", city, roomId],
    enabled: needsFallback && !!city.trim(),
    queryFn: async () => {
      const { data, error } = await db
        .from("rooms")
        .select(ROOM_FIELDS_WITH_PROPERTY)
        .eq("properties.city", city)
        .eq("properties.is_hidden", false)
        .eq("is_hidden", false)
        .neq("id", roomId)
        .limit(12);
      if (error) throw error;
      return ((data || []) as unknown as SiblingRoom[]).filter(
        isPubliclyListable
      );
    },
  });

  return {
    rooms: (needsFallback ? cityRooms : flatRooms).slice(0, 4),
    isCityFallback: needsFallback,
  };
}

const MoreRoomsInFlat = ({
  roomId,
  apartmentId,
  city,
}: MoreRoomsInFlatProps) => {
  const { t, language } = useLanguage();
  const de = language === "de";
  const { rooms, isCityFallback: needsFallback } = useMoreRoomsInFlat({
    roomId,
    apartmentId,
    city,
  });
  if (rooms.length === 0) return null;

  // Non-breaking spaces keep "15. Nov." together when the pill wraps on a
  // narrow two-column card, so it breaks before the date, not inside it.
  const formatDate = (iso: string): string =>
    new Date(`${iso}T00:00:00`)
      .toLocaleDateString(de ? "de-DE" : "en-GB", {
        day: "numeric",
        month: "short",
      })
      .replace(/\s/g, "\u00A0");

  return (
    <Card className="p-6">
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-2xl font-bold text-foreground">
          {needsFallback
            ? t(
                "property.moreRooms.cityHeading",
                de ? `Weitere Zimmer in ${city}` : `Other rooms in ${city}`
              )
            : t(
                "property.moreRooms.flatHeading",
                de
                  ? "Weitere Zimmer in dieser Wohnung"
                  : "More rooms in this flat"
              )}
        </h2>
        {needsFallback && city.trim() ? (
          <Link
            to={`/rooms/${city.toLowerCase()}`}
            className="text-sm font-medium text-turkis-strong hover:underline"
          >
            {t("property.moreRooms.viewAll", de ? "Alle ansehen" : "View all")}
          </Link>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {rooms.map((sibling) => {
          const { listingAvailable, nextAvailableDate } =
            getListingAvailability({
              available: sibling.available !== false,
              available_from: sibling.available_from,
              next_available_date: sibling.next_available_date,
            });
          const image = firstImageUrl(sibling.images);
          const meta = [
            sibling.size_sqm ? `${Number(sibling.size_sqm)} m²` : null,
            sibling.floor,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <Link
              key={sibling.id}
              to={`/property/${sibling.id}`}
              className={`group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                listingAvailable ? "" : "opacity-60"
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-black/5 bg-turkis-fill">
                {image ? (
                  <img
                    src={image}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <BedDouble
                      className="h-6 w-6 text-turkis-strong"
                      aria-hidden="true"
                    />
                  </div>
                )}
                <span className="absolute left-2 top-2 rounded-2xl border border-black/5 bg-background/90 px-2 py-0.5 text-[11px] font-medium leading-tight text-foreground">
                  {listingAvailable
                    ? t(
                        "properties.available",
                        de ? "Sofort verfügbar" : "Available now"
                      )
                    : nextAvailableDate
                      ? de
                        ? `Verfügbar ab ${formatDate(nextAvailableDate)}`
                        : `Available from ${formatDate(nextAvailableDate)}`
                      : t("properties.unavailable", de ? "Belegt" : "Occupied")}
                </span>
              </div>
              <p className="mt-2 truncate font-medium text-foreground">
                {sibling.title || (de ? "Zimmer" : "Room")}
              </p>
              {meta ? (
                <p className="truncate text-sm text-muted-foreground">{meta}</p>
              ) : null}
              {Number(sibling.price) > 0 ? (
                <p className="text-sm text-foreground">
                  €{Number(sibling.price)}
                  <span className="text-muted-foreground">
                    {de ? " / Monat" : " / month"}
                  </span>
                </p>
              ) : null}
            </Link>
          );
        })}
      </div>
    </Card>
  );
};

export default MoreRoomsInFlat;
